import React, { FC, useMemo } from 'react';

import { AggregationValue, AggregationValueWithByCycle } from '~src/interfaces/heatmap.interface';
import { Scale, ScaleVisualization } from '~src/interfaces/scale.interface';
import {
  calcIndex,
  calcTwoDigitsRating,
  getIndexScaleLength,
  getScaleLength,
} from '~src/modules/reports/services/ratingTransformations';
import DisplayScale from '~src/modules/reports/shared/components/ratings/displayScale/DisplayScale';
import { compareColorSet, mcqColor } from '~src/styles/colors/compareColorSet';

import { translateContent } from '~src/services/translationServices';
import { RatingTileContainer, TileBack, TileFront, TileInner } from './ratingTile.sc';
import {useTranslation} from "react-i18next";

interface Props {
  zoomOnHover: boolean;
  answer: AggregationValueWithByCycle | AggregationValue;
  scale: Scale | null;
}

const RatingTile: FC<Props> = (props) => {
  const { t } = useTranslation();
  const ratingIndex = useMemo(
    () =>
      props.scale?.visualization === ScaleVisualization.McqOptions
        ? props.answer.top_chosen_option
        : calcIndex(props.answer.average, props.scale),
    [props.answer.average, props.answer.top_chosen_option, props.scale],
  );
  const scaleLength = useMemo(() => (props.scale ? getScaleLength(props.scale) : null), [props.scale]);

  let ratingColor = null;
  if (props.scale?.visualization === ScaleVisualization.McqOptions) {
    ratingColor = mcqColor;
  } else if (props.scale && ratingIndex !== null) {
    ratingColor = compareColorSet[getIndexScaleLength(props.scale)][ratingIndex];
  }

  const index =
    props.scale?.visualization === ScaleVisualization.McqOptions
      ? props.answer.top_chosen_option
      : calcIndex(props.answer.average, props.scale);

  const isMcq = props.scale?.visualization === ScaleVisualization.McqOptions;

  const labels = (isMcq ? props.scale?.multipleChoiceOptions : props.scale?.labels) || [];

  const labelTextCount =
    translateContent(
      labels[isMcq ? props.answer.top_chosen_option : calcIndex(props.answer.average, props.scale)],
    ).length > 50;
  const rating = isMcq ? props.answer.top_chosen_option : props.answer.average;

  return (
    <RatingTileContainer $zoomOnHover={props.zoomOnHover}>
      {ratingColor && props.scale && scaleLength && (
        <TileInner>
          <TileFront $color={ratingColor}>
            <DisplayScale
              blank
              onlyText
              selectedLabelIndex={index}
              type={props.scale.visualization}
              labels={labels}
              maxRating={getScaleLength(props.scale)}
              rating={rating}
              small={Boolean(labelTextCount)}
            />
          </TileFront>
          <TileBack $color={ratingColor}>
            {isMcq ? null : (
              <div>
                <span>{calcTwoDigitsRating(scaleLength, rating, props.scale.visualization)}</span>
                <span> / </span>
                <span>
                  {props.scale.visualization === ScaleVisualization.Nps ? scaleLength - 1 : scaleLength}
                </span>
              </div>
            )}
            <div>
              <span data-translation="x.compare.tile.answers">
                {t('x.compare.tile.answers', { count: props.answer.number_of_answers })}
              </span>
            </div>
          </TileBack>
        </TileInner>
      )}
    </RatingTileContainer>
  );
};

export default React.memo(RatingTile);
